import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useSidebar } from '../lib/sidebarContext';
import MessageList from '../components/messaging/MessageList';
import MessageInput from '../components/messaging/MessageInput';
import TypingIndicator from '../components/messaging/TypingIndicator';
import ChannelSettingsPanel from '../components/channel/ChannelSettingsPanel';
import SearchPanel from '../components/search/SearchPanel';
import UserPopover from '../components/user/UserPopover';
import Lightbox from '../components/file/Lightbox';
import { useChannelEpochKey } from '../hooks/useChannelEpochKey';
import { useSocketStore } from '../lib/socket';
import { useLeaveWhenChannelGone } from '../hooks/useLeaveWhenChannelGone';

export default function ChannelView() {
  const { channelId, orgSlug } = useParams<{ channelId: string; orgSlug: string }>();
  const navigate = useNavigate();
  const { openSidebar } = useSidebar();
  const queryClient = useQueryClient();
  const [showSettings, setShowSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [editingMessage, setEditingMessage] = useState<{ id: string; content: string } | null>(
    null,
  );
  const [popover, setPopover] = useState<{
    user: { id: string; displayName: string; avatarUrl: string | null; email?: string };
    position: { x: number; y: number };
  } | null>(null);
  const [lightbox, setLightbox] = useState<{
    images: { url: string; filename: string }[];
    index: number;
  } | null>(null);

  const currentUserId = useAuthStore((s) => s.user?.id);
  // Re-run the CHANNEL_DELETE listener effect when the socket is replaced (see socketId in socket.ts).
  const socketId = useSocketStore((s) => s.socketId);

  const { data: channel, error: channelError } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: async () => {
      const { data } = await api.get(`/api/v1/channels/${channelId}`);
      return data.data;
    },
    enabled: !!channelId,
    retry: false,
  });

  // Ensure the symmetric channel key is fetched and cached for E2EE.
  // useChannelEpochKey drives the pending-key pickup + ack loop and
  // populates the epoch-aware key ring in shared, which
  // decryptChannelMessage uses for any message stamped with a non-null
  // keyEpoch (including legacy epoch=0 rows).
  //
  // The resolved key bytes are forwarded into MessageList so its decrypt
  // useMemo re-runs when the ring is populated. Without this, the
  // epochKeyRing singleton mutates invisibly to React: the first render
  // memoises a "[Message could not be decrypted]" cache and never busts
  // it, even after the ring fills in.
  const { key: channelEpochKey } = useChannelEpochKey(channelId ?? '');

  const channelTitle = useMemo(() => {
    if (!channel) return 'Loading...';
    if (channel.type === 'DM' && channel.members?.length) {
      const peers = channel.members
        .filter((m: { userId: string }) => m.userId !== currentUserId)
        .map((m: { user: { displayName: string } }) => m.user.displayName);
      if (peers.length === 0) return channel.name.replace(/-/g, ' ');
      // 1:1 DM: show full name; 3+ group: show first names
      if (peers.length === 1) return peers[0];
      return peers.map((n: string) => n.split(' ')[0]).join(', ');
    }
    return channel.name.replace(/-/g, ' ');
  }, [channel, currentUserId]);

  // userId → displayName for reaction tooltips ("Alice, Bob reacted with 👍").
  // Memoised so MessageItem's memo comparator sees a stable reference until
  // the member list actually changes.
  const memberNames = useMemo<Record<string, string> | undefined>(() => {
    const members = channel?.members as
      | { userId: string; user?: { displayName?: string } }[]
      | undefined;
    if (!members?.length) return undefined;
    const map: Record<string, string> = {};
    for (const m of members) {
      if (m.user?.displayName) map[m.userId] = m.user.displayName;
    }
    return map;
  }, [channel?.members]);

  const { data: orgs } = useQuery({
    queryKey: ['orgs'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/orgs');
      return data.data;
    },
  });

  const currentOrg = orgs?.find((o: { slug: string }) => o.slug === orgSlug) || orgs?.[0];

  // Guard: if the API returned 403/404 for this channel the user has no access.
  // Navigate back to the org root instead of silently rendering a broken view.
  // Navigation is a side effect — running it during render is illegal in
  // React (and double-fires under StrictMode), so it lives in an effect.
  const channelErrorStatus = channelError
    ? (channelError as { response?: { status?: number } }).response?.status
    : undefined;
  const channelForbidden = channelErrorStatus === 403 || channelErrorStatus === 404;
  useEffect(() => {
    // replace: Back must not land on the forbidden channel and re-trigger
    // this redirect loop.
    if (channelForbidden) navigate(`/app/${orgSlug ?? ''}`, { replace: true });
  }, [channelForbidden, navigate, orgSlug]);

  // Leave the view when the channel is deleted or this user is removed from it.
  const leaveChannel = useCallback(() => navigate(`/app/${orgSlug ?? ''}`), [navigate, orgSlug]);
  useLeaveWhenChannelGone(channelId, socketId, leaveChannel);

  if (!channelId) return null;
  if (channelForbidden) return null;

  async function handleStartDm(targetUserId: string) {
    if (!currentOrg) return;
    try {
      const { data } = await api.post(`/api/v1/orgs/${currentOrg.id}/dm`, { targetUserId });
      queryClient.invalidateQueries({ queryKey: ['channels', currentOrg.id] });
      navigate(`/app/${currentOrg.slug}/${data.data.id}`);
    } catch {
      // DM creation failed
    }
    setPopover(null);
    setShowSettings(false);
  }

  function handleEditStart(messageId: string, content: string) {
    setEditingMessage({ id: messageId, content });
  }

  function handleImageClick(url: string, _index: number) {
    setLightbox({ images: [{ url, filename: 'image' }], index: 0 });
  }

  function handleUserClick(
    user: { id: string; displayName: string; avatarUrl: string | null; email?: string },
    position: { x: number; y: number },
  ) {
    setPopover({ user, position });
  }

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center border-b border-warm-border-light bg-warm-white px-3 py-3 md:px-5 dark:border-warm-dark-border dark:bg-warm-dark-surface">
          <button
            onClick={openSidebar}
            className="mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-warm-gray-400 hover:bg-warm-gray-100 hover:text-warm-gray-700 dark:hover:bg-white/5 dark:hover:text-white md:hidden"
            aria-label="Open menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-body-sm font-semibold text-warm-gray-800 dark:text-white md:text-h3">
              {channelTitle}
            </h2>
            {channel?.topic && (
              <p className="truncate text-caption text-warm-gray-400 dark:text-warm-gray-500">
                {channel.topic}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowSearch(!showSearch);
                setShowSettings(false);
              }}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition ${showSearch ? 'bg-primary-light text-primary dark:bg-primary-dark dark:text-white' : 'text-warm-gray-400 hover:bg-warm-gray-100 hover:text-warm-gray-700 dark:hover:bg-white/5 dark:hover:text-white'}`}
              title="Search"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
            <button
              onClick={() => {
                setShowSettings(!showSettings);
                setShowSearch(false);
              }}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition ${showSettings ? 'bg-primary-light text-primary dark:bg-primary-dark dark:text-white' : 'text-warm-gray-400 hover:bg-warm-gray-100 hover:text-warm-gray-700 dark:hover:bg-white/5 dark:hover:text-white'}`}
              title="Channel details"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
            <span className="hidden text-body-sm text-warm-gray-400 dark:text-warm-gray-500 sm:inline">
              {channel?.members?.length || 0} members
            </span>
          </div>
        </div>

        <MessageList
          channelId={channelId}
          channelType={channel?.type ?? 'PRIVATE'}
          epochKeySignal={channelEpochKey}
          onEditStart={handleEditStart}
          onImageClick={handleImageClick}
          onUserClick={handleUserClick}
          memberNames={memberNames}
        />
        <TypingIndicator channelId={channelId} />
        <MessageInput
          channelId={channelId}
          channelType={channel?.type}
          editingMessage={editingMessage}
          onCancelEdit={() => setEditingMessage(null)}
          dmPeerUserId={
            channel?.type === 'DM'
              ? (channel.members?.find((m: { userId: string }) => m.userId !== currentUserId)
                  ?.userId ?? null)
              : null
          }
        />
      </div>

      {showSettings && (
        <div className="fixed inset-0 z-20 md:static md:z-auto md:inset-auto">
          <ChannelSettingsPanel
            channelId={channelId}
            onClose={() => setShowSettings(false)}
            onStartDm={handleStartDm}
            onDeleted={() => navigate(`/app/${orgSlug ?? ''}`)}
          />
        </div>
      )}

      {showSearch && (
        <div className="fixed inset-0 z-20 md:static md:z-auto md:inset-auto">
          <SearchPanel
            channelId={channelId}
            onClose={() => setShowSearch(false)}
            onScrollToMessage={() => {}}
          />
        </div>
      )}

      {popover && (
        <UserPopover
          user={popover.user}
          position={popover.position}
          orgId={currentOrg?.id}
          onClose={() => setPopover(null)}
          onDm={() => handleStartDm(popover.user.id)}
        />
      )}

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          currentIndex={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(i) => setLightbox((prev) => (prev ? { ...prev, index: i } : null))}
        />
      )}
    </div>
  );
}
